import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModals } from "../../context/ModalContext";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { formatEmails, formatPhoneNumbers, invalidateContactsAndClients, menuPropsModal } from "../../utils";
import { TextFieldComponent } from "../common/TextFieldComponent";
import { ContactInformationFields } from "../shared/components/forms/contactInformation/ContactInformationFields";
import { colors as color } from "../ui/colors";
import { useDialog } from "../ui/dialogs/useDialog";
import { SASButtonStyles, SASSelectStyles } from "../ui/styles/inputStyles";
import { useClients } from "./hooks/useClients";

export const ClientForm = ({
  client = null,
  onUpdate = () => {},
  mode = "create", // "create" | "update" | "view"
  onCancel = () => {},
  sx = {},
  clientUid,
  contact = null,
  clientForm = null,
  onSubmitCreate = null,
  isProspect = false,
}) => {
  const isView = mode === "view";
  const isCreate = mode === "create";
  const isUpdate = mode === "update";

  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const queryClient = useQueryClient();

  const { editClient } = useClients();
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    reset,
    setValue,
    unregister,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      alias: "",
      emails: [{ value: "" }],
      phones: [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "" }],
      type: "residencial",
      clientCompanyName: "",
      occupantPays: null,
    },
  });

  const [contactState, setContactState] = useState({ emails: [], phones: [], isValid: true });
  const [showContactErrors, setShowContactErrors] = useState(false);
  const handleContactInfoChange = useCallback((state) => {
    setContactState(state);
  }, []);

  const isInitialized = useRef(false);
  const { showDialog, DialogComponent } = useDialog();
  const clientType = watch("type");

  useEffect(() => {
    isInitialized.current = false;
  }, [client?.uid, contact?.uid, client?.updatedAt]);

  useEffect(() => {
    if (isInitialized.current) return;

    const source = isCreate ? (Object.keys(clientForm || {}).length > 0 ? clientForm : null) : client;

    if (source && Object.keys(source).length > 0) {
      let emails = [{ email: "", tag: "other", isDefault: false }];
      if (Array.isArray(source.emails) && source.emails.length > 0) {
        emails = source.emails.map((e) =>
          typeof e === "string"
            ? { email: e, tag: "other", isDefault: false }
            : {
                email: e?.value || e?.email || "",
                tag: e?.tag || "other",
                isDefault: !!e?.isDefault,
              },
        );
      } else if (source.email) {
        emails = [{ email: source.email, tag: "other", isDefault: true }];
      }

      let phones = [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "" }];
      if (Array.isArray(source.phoneNumbers) && source.phoneNumbers.length > 0) {
        phones = source.phoneNumbers.map((p) => ({
          countryCode: p.countryCode || "US",
          dialCode: p.dialCode || "+1",
          phone: p.phone || "",
          phoneNumber: (p.dialCode || "+1") + (p.phone || ""),
          tag: p.tag || undefined,
          isDefault: p.isDefault || undefined,
        }));
      } else if (Array.isArray(source.phones) && source.phones.length > 0) {
        phones = source.phones.map((p) => ({
          countryCode: p.countryCode || "US",
          dialCode: p.dialCode || "+1",
          phone: p.phone || "",
          phoneNumber: (p.dialCode || "+1") + (p.phone || ""),
          tag: p.tag || undefined,
          isDefault: p.isDefault || undefined,
        }));
      } else if (source.phone) {
        phones = [
          {
            countryCode: source.countryCode || "US",
            dialCode: source.dialCode || "+1",
            phone: source.phone,
            phoneNumber: (source.dialCode || "+1") + (source.phone || ""),
          },
        ];
      }

      const formData = {
        firstName: source.firstName || "",
        lastName: source.lastName || "",
        alias: source.alias || "",
        emails,
        phones,
        type: source.type || "residencial",
        clientCompanyName: source.clientCompanyName || "",
        occupantPays: source.occupantPays === true ? true : source.occupantPays === false ? false : null,
      };
      reset(formData);
      isInitialized.current = true;
    } else if (isCreate && contact) {
      let emails = [{ email: "" }];
      if (contact.email) {
        emails = [{ email: contact.email }];
      }

      let phones = [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "" }];
      if (Array.isArray(contact.phoneNumbers) && contact.phoneNumbers.length > 0) {
        phones = contact.phoneNumbers
          .map((p) => {
            const digits = p.number.replace(/\D/g, "");
            let dialCode = "+1";
            let phone = digits;
            if (digits.startsWith("1") && digits.length === 11) {
              phone = digits.substring(1);
            }
            return {
              countryCode: "US",
              dialCode,
              phone,
              phoneNumber: dialCode + phone,
              tag: p.label || "mobile",
              isDefault: p.isPrimary || false,
            };
          })
          .filter((p) => p.phone);
      }

      const formData = {
        firstName: contact.name || "",
        lastName: contact.lastName || "",
        alias: "",
        emails,
        phones,
        type: "residencial",
        clientCompanyName: "",
        occupantPays: null,
      };
      reset(formData);
      isInitialized.current = true;
    } else {
      isInitialized.current = true;
    }
  }, [isCreate, client, reset, clientForm, contact]);

  const onCBSubmit = async (data) => {
    if (isView) return;

    const mappedEmails = formatEmails(contactState.emails);
    const mappedPhoneNumbers = formatPhoneNumbers(contactState.phones);

    const hasEmail = mappedEmails.length > 0;
    const hasPhone = mappedPhoneNumbers.length > 0;

    if (!hasEmail && !hasPhone) {
      setShowContactErrors(true);
      showSnackbar("You have not provided an email or phone number.", "error", 2500, "bottom-center");
      return;
    }

    if (!contactState.isValid) {
      setShowContactErrors(true);
      return;
    }

    const primaryEmail = mappedEmails[0]?.email || data?.email || null;
    const primaryPhone = mappedPhoneNumbers[0] || null;
    const normalizedPhone = primaryPhone?.phone || data?.phone || "";
    const normalizedDialCode = primaryPhone?.dialCode || data?.dialCode || "+1";
    const normalizedCountryCode = primaryPhone?.countryCode || data?.countryCode || "US";

    const occupantPays = data.type === "commercial" ? null : data.occupantPays === true ? true : data.occupantPays === false ? false : null;

    const submitData = {
      ...data,
      occupantPays,
      email: primaryEmail,
      phone: normalizedPhone,
      dialCode: normalizedDialCode,
      countryCode: normalizedCountryCode,
      emails: mappedEmails,
      phones: mappedPhoneNumbers,
    };

    if (isCreate) {
      onSubmitCreate?.(submitData);
      return;
    }

    if (isUpdate) {
      try {
        const { email, phone, phones, emails, ...cleanedData } = submitData;

        const payload = {
          ...cleanedData,
          email: primaryEmail,
          phone: normalizedPhone,
          dialCode: normalizedDialCode,
          countryCode: normalizedCountryCode,
          clientCompanyName: clientType === "commercial" ? cleanedData.clientCompanyName || client?.clientCompanyName || null : client?.clientCompanyName || null,
          alias: cleanedData.alias ?? client?.alias ?? null,
          emails: mappedEmails,
          phoneNumbers: mappedPhoneNumbers,
        };

        const res = await editClient(clientUid, payload);

        const ok = res?.ok === true;

        await invalidateContactsAndClients(queryClient);
        await queryClient.invalidateQueries({ queryKey: ["job"] });
        if (ok) {
          showSnackbar("Client updated successfully", "success");
          await onUpdate?.();
        } else {
          showSnackbar("Update failed. Please try again and check the form.", "error");
        }
      } catch (err) {
        showSnackbar("Update failed. Please try again and check the form.", "error");
      }
    }
  };

  const companyTypes = [
    { name: "Residential", id: "residencial" },
    { name: "Commercial", id: "commercial" },
  ];

  const SectionTitle = ({ children, icon }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: color.orange?.base || "#FF6B35",
              borderRadius: 2,
              mr: 2,
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: color.gray?.[800] || "var(--fx-surface-text, #0F172A)",
            letterSpacing: 0.5,
          }}
        >
          {children}
        </Typography>
      </Box>
      <Divider
        sx={{
          borderColor: color.gray?.[200] || "var(--fx-surface-border, #E2E8F0)",
          opacity: 0.8,
        }}
      />
    </Box>
  );

  const REGEX_LETTERS_ONLY = /^[A-Za-zÀ-ÿ\s]+$/;
  const REGEX_EMAIL_ONLY = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return (
    <Box
      id="client-form-general-info"
      component="form"
      onSubmit={handleSubmit(onCBSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        width: client ? "100%" : "80%",
        gap: 4,
        py: 2,
        ...sx,
      }}
    >
      {/* Client information */}
      <Box sx={{ width: "100%" }}>
        <SectionTitle icon>{isProspect ? "Prospect Information" : "Client Information"}</SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                color: color.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                fontWeight: 600,
                mb: 1,
              }}
            >
              {isProspect ? "Prospect Type" : "Client Type"}
            </Typography>

            <Controller
              name="type"
              control={control}
              rules={{ required: isProspect ? "Prospect type is required" : "Client type is required" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error} sx={SASSelectStyles}>
                  <InputLabel id="client-type-label">{isProspect ? "Prospect Type" : "Client Type"}</InputLabel>
                  <Select
                    {...field}
                    labelId="client-type-label"
                    label={isProspect ? "Prospect Type" : "Client Type"}
                    MenuProps={menuPropsModal}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={isView}
                  >
                    {companyTypes.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {clientType !== "commercial" && (
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <Controller
                name="occupantPays"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 40 }}>
                    <FormControlLabel
                      sx={{ m: 0, color: "text.secondary" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={field.value === true}
                          indeterminate={field.value == null}
                          onChange={(_, checked) => field.onChange(checked)}
                          disabled={isView}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" title="Iowa only. Exempt when the person who pays lives in the home. A dash means not answered.">
                          Occupant pays
                        </Typography>
                      }
                    />
                    {!isView && field.value != null && (
                      <Button
                        type="button"
                        size="small"
                        onClick={() => field.onChange(null)}
                        sx={{ minWidth: 0, px: 0.5, textTransform: "none", color: "text.disabled", fontSize: "0.75rem" }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                )}
              />
            </Grid>
          )}

          {clientType === "commercial" && (
            <Grid item xs={12} md={6}>
              <TextFieldComponent
                name="clientCompanyName"
                errors={errors}
                register={register("clientCompanyName", {
                  required: {
                    value: isProspect ? !watch("firstName") : false,
                    message: "Company name is required",
                  },
                  minLength: { value: 2, message: "Minimum of two characters" },
                })}
                label="Company Name"
                inputLabel="Company Name"
                disabled={isView}
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="alias"
              errors={errors}
              register={register("alias", {
                minLength: { value: 2, message: "Minimum of two characters" },
              })}
              label="Alias"
              inputLabel="Alias"
              disabled={isView}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="firstName"
              errors={errors}
              register={register("firstName", {
                required: {
                  value: !isProspect || (clientType === "commercial" ? !watch("clientCompanyName") : true),
                  message: "First name is required",
                },
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: {
                  value: REGEX_LETTERS_ONLY,
                  message: "Only letters are allowed",
                },
              })}
              label="First Name"
              inputLabel="First Name"
              disabled={isView}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextFieldComponent
              name="lastName"
              errors={errors}
              register={register("lastName", {
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: {
                  value: REGEX_LETTERS_ONLY,
                  message: "Only letters are allowed",
                },
              })}
              label="Last Name"
              inputLabel="Last Name"
              disabled={isView}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Contact information */}
      <ContactInformationFields
        key={isInitialized.current ? clientUid || "initialized" : "loading"}
        initialEmails={getValues("emails")}
        initialPhones={getValues("phones")}
        onChange={handleContactInfoChange}
        disabled={isView}
        requireEither={true}
        showErrors={showContactErrors}
      />
      {/* Footer actions:
          - create mode: handled elsewhere (wizard) so nothing here (same behavior)
          - update mode with client: show Cancel/Update
          - view mode with client: show only Close (uses onCancel)
      */}
      {client && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 4,
            width: "100%",
          }}
        >
          <Button
            onClick={() => {
              if (clientUid) {
                closeByKey(`update-client-${clientUid}`);
                closeByKey("Client-Details-Modal");
              }
              onCancel?.();
            }}
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: color.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
              border: `1px solid ${color.gray?.[300] || "var(--fx-surface-borderstrong, #CBD5E1)"}`,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              "&:hover": {
                backgroundColor: color.gray?.[100] || "var(--fx-surface-sunken, #F8FAFC)",
                borderColor: color.gray?.[400] || "var(--fx-surface-borderstrong, #CBD5E1)",
              },
            }}
          >
            {isView ? "Close" : "Cancel"}
          </Button>

          {!isView && (
            <Button
              type="submit"
              sx={{
                ...SASButtonStyles,
              }}
            >
              Update
            </Button>
          )}
        </Box>
      )}

      {DialogComponent}
    </Box>
  );
};
